/**
 * React Query hooks for 患者リスト(施術師毎) / Patient List by Practitioner.
 * Search + massager dropdown + insurer type dropdown.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  searchVisitPatients,
  getVisitPatientMassagers,
  getVisitPatientInsurerTypes,
} from '@/services/main/visitPatient';
import type {
  VisitPatientSearchParams,
  VisitPatientSearchResponse,
  VisitPatientMassagersResponse,
  VisitPatientMassager,
  VisitPatientInsurerTypesResponse,
  VisitPatientInsurerType,
} from '@/pages/main/visitPatient/types';

function getServiceTypeFallback(): string {
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

function useServiceType(): string {
  const theme = useTheme();
  return theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
    ? theme.serviceType
    : getServiceTypeFallback();
}

/**
 * Grouped-by-patient search. Pass null to skip (initial state before first submit).
 */
export function useVisitPatientSearch(params: VisitPatientSearchParams | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery<
    VisitPatientSearchResponse,
    Error
  >({
    queryKey: [
      'visit-patients',
      'search',
      tenant,
      clinicNumber,
      serviceType,
      params,
    ],
    queryFn: () => searchVisitPatients(tenant, clinicNumber, params!, serviceType),
    enabled: !!tenant && !!clinicNumber && params !== null,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const responseData = data?.data;
  const rows = responseData?.data ?? [];
  const pagination = responseData?.pagination ?? null;
  const allPatientIds = responseData?.all_patient_ids ?? [];

  return {
    rows,
    pagination,
    allPatientIds,
    isLoading: isLoading || isFetching,
    isError,
    isPending,
    refetch,
    error,
  };
}

/**
 * Massager dropdown — reloads when 訪問年月日 FROM (targetDate) changes.
 */
export function useVisitPatientMassagers(targetDate?: string | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    VisitPatientMassagersResponse,
    Error
  >({
    queryKey: ['visit-patients', 'massagers', tenant, clinicNumber, serviceType, targetDate],
    queryFn: () => getVisitPatientMassagers(tenant, clinicNumber, serviceType, targetDate),
    enabled: !!tenant && !!clinicNumber,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const massagers: VisitPatientMassager[] = data?.data?.data ?? [];

  return { massagers, isLoading, isError, isPending, refetch, error };
}

/**
 * Insurer type dropdown — master data, loaded once.
 */
export function useVisitPatientInsurerTypes() {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    VisitPatientInsurerTypesResponse,
    Error
  >({
    queryKey: ['visit-patients', 'insurer-types', tenant, clinicNumber, serviceType],
    queryFn: () => getVisitPatientInsurerTypes(tenant, clinicNumber, serviceType),
    enabled: !!tenant && !!clinicNumber,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const insurerTypes: VisitPatientInsurerType[] = data?.data?.data ?? [];

  return { insurerTypes, isLoading, isError, isPending, refetch, error };
}
